import React, { useState, useEffect } from 'react';

import { getConfig } from '../../runtimeConfig';
import { cachedFetch } from '../../cacheManager';
const API_BASE = getConfig().REACT_APP_AI_API;

interface TeamsConfig {
  TEAMS_CHANNEL_WEBHOOK?: string;
  TEAMS_WEBHOOK_URL?: string;
  TEAMS_SCHEDULE_DAY?: string;
  TEAMS_SCHEDULE_TIME?: string;
}

interface TeamsSettingsProps {
  onClose: () => void;
  token: string;
}

const COLORS = {
  primary: '#118DFF',
  secondary: '#1B2631',
  success: '#6B8E23',
  warning: '#FFC000',
  danger: '#C83D3D',
  cardBg: '#FFFFFF',
  text: '#252423',
  textLight: '#605E5C',
  border: '#E8E8E8',
  lightGray: '#F2F2F2',
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: COLORS.cardBg,
    borderRadius: '12px',
    width: '600px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    padding: '20px 24px',
    borderBottom: `1px solid ${COLORS.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.text,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: COLORS.textLight,
  },
  content: {
    padding: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inputGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: COLORS.textLight,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  helpText: {
    fontSize: '11px',
    color: COLORS.textLight,
    marginTop: '4px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  actions: {
    padding: '16px 24px',
    borderTop: `1px solid ${COLORS.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
  },
  btn: {
    padding: '10px 20px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
  },
  btnPrimary: {
    backgroundColor: COLORS.primary,
    color: 'white',
  },
  btnSecondary: {
    backgroundColor: COLORS.lightGray,
    color: COLORS.text,
  },
  btnSuccess: {
    backgroundColor: COLORS.success,
    color: 'white',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
  },
  infoBox: {
    backgroundColor: '#E3F2FD',
    border: '1px solid #2196F3',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
  },
  infoTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1565C0',
    marginBottom: '6px',
  },
  infoText: {
    fontSize: '12px',
    color: '#1565C0',
    lineHeight: '1.5',
  },
  testResult: {
    marginTop: '12px',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
  },
};

const TeamsSettings: React.FC<TeamsSettingsProps> = ({ onClose, token }) => {
  const [config, setConfig] = useState<TeamsConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const [channelWebhook, setChannelWebhook] = useState('');
  const [directWebhook, setDirectWebhook] = useState('');
  const [scheduleDay, setScheduleDay] = useState('1'); // Monday
  const [scheduleTime, setScheduleTime] = useState('08:00');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const data = await cachedFetch(`${API_BASE}/teams/config`, { ttl: 60000 });
      if (data.success && data.config) {
        setConfig(data.config);
        setChannelWebhook(data.config.TEAMS_CHANNEL_WEBHOOK || '');
        setDirectWebhook(data.config.TEAMS_WEBHOOK_URL || '');
        setScheduleDay(data.config.TEAMS_SCHEDULE_DAY || '1');
        setScheduleTime(data.config.TEAMS_SCHEDULE_TIME || '08:00');
      }
    } catch (error) {
      console.error('Failed to load Teams config:', error);
    }
    setLoading(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/teams/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channelWebhook,
          directWebhook,
          scheduleDay,
          scheduleTime,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTestResult({ success: true, message: 'Configuration saved successfully!' });
      } else {
        setTestResult({ success: false, message: data.error || 'Failed to save' });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message });
    }
    setSaving(false);
  };

  const testSendReminder = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/teams/send-weekly-reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          week: new Date().getWeek(),
          year: new Date().getFullYear(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        const names = data.supervisors?.map((s: any) => s.name || s).join(', ') || 'None';
        const failedMsg = data.failed?.length > 0 ? ` (${data.failed.length} failed)` : '';
        setTestResult({
          success: true,
          message: `Emails sent to ${data.supervisors?.length || 0} supervisors: ${names}${failedMsg}`,
        });
      } else {
        setTestResult({ success: false, message: data.error || 'Failed to send' });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message });
    }
    setTesting(false);
  };

  const testSendSummary = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/teams/send-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          week: new Date().getWeek(),
          year: new Date().getFullYear(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTestResult({
          success: true,
          message: `Summary sent for ${data.warehouses?.length || 0} warehouses`,
        });
      } else {
        setTestResult({ success: false, message: data.error || 'Failed to send' });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message });
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            Loading Teams configuration...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.title}>
            <span style={{ fontSize: '24px' }}>📧</span>
            Weekly Notifications Settings
          </div>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={styles.content}>
          {/* Info Box */}
          <div style={styles.infoBox}>
            <div style={styles.infoTitle}>How it works</div>
            <div style={styles.infoText}>
              <strong>Email to Supervisors:</strong><br />
              1. Supervisors receive weekly EMAIL reminders<br />
              2. Email contains previous week data + link to dashboard<br />
              3. They click link → fill in inbound/outbound → saved<br /><br />
              <strong>Teams Channel Summary:</strong><br />
              4. Summary card posted to Teams channel for management
            </div>
          </div>

          {/* Email Notification Info */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span>📧</span> Email Notifications (Supervisors)
            </div>
            <div style={{ ...styles.infoBox, backgroundColor: '#E8F5E9', borderColor: '#4CAF50' }}>
              <div style={{ ...styles.infoText, color: '#2E7D32' }}>
                Emails are sent automatically to supervisors based on their email addresses in the Users table.
                Supervisors with role "warehouse_supervisor" will receive individual emails with their warehouse data.
              </div>
            </div>
          </div>

          {/* Teams Channel Webhook */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span>📱</span> Teams Channel (Optional - for summaries)
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Teams Channel Webhook URL</label>
              <input
                type="text"
                style={styles.input}
                value={channelWebhook}
                onChange={(e) => setChannelWebhook(e.target.value)}
                placeholder="https://halagroupsa.webhook.office.com/..."
              />
              <div style={styles.helpText}>
                Used to post weekly summary cards to the Teams channel (visible to all channel members)
              </div>
            </div>
          </div>

          {/* Schedule Configuration */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span>📅</span> Automatic Schedule
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Send reminders on</label>
                <select
                  style={styles.select}
                  value={scheduleDay}
                  onChange={(e) => setScheduleDay(e.target.value)}
                >
                  <option value="0">Sunday</option>
                  <option value="1">Monday</option>
                  <option value="2">Tuesday</option>
                  <option value="3">Wednesday</option>
                  <option value="4">Thursday</option>
                  <option value="5">Friday</option>
                  <option value="6">Saturday</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>At time</label>
                <input
                  type="time"
                  style={styles.input}
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </div>
            </div>
            <div style={styles.helpText}>
              Automatic reminders require ENABLE_TEAMS_SCHEDULER=true in server environment
            </div>
          </div>

          {/* Test Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span>🧪</span> Test Notifications
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={testSendReminder}
                disabled={testing}
              >
                {testing ? '⏳ Sending...' : '📧 Send Email to Supervisors'}
              </button>

              <button
                style={{ ...styles.btn, ...styles.btnSecondary }}
                onClick={testSendSummary}
                disabled={testing || !channelWebhook}
              >
                {testing ? '⏳ Sending...' : '📱 Send Summary to Teams'}
              </button>
            </div>

            {testResult && (
              <div
                style={{
                  ...styles.testResult,
                  backgroundColor: testResult.success ? '#E8F5E9' : '#FFEBEE',
                  color: testResult.success ? '#2E7D32' : '#C62828',
                }}
              >
                {testResult.success ? '✅' : '❌'} {testResult.message}
              </div>
            )}
          </div>
        </div>

        <div style={styles.actions}>
          <button
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary }}
            onClick={saveConfig}
            disabled={saving}
          >
            {saving ? '⏳ Saving...' : '💾 Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Add getWeek to Date prototype if not exists
declare global {
  interface Date {
    getWeek(): number;
  }
}

if (!Date.prototype.getWeek) {
  Date.prototype.getWeek = function (): number {
    const d = new Date(Date.UTC(this.getFullYear(), this.getMonth(), this.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };
}

export default TeamsSettings;
